import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { PageEvent as PageEvent } from '@angular/material/paginator';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/shared/components';
import { Crew, Labor, Project } from 'src/app/shared/models';
import { AppErrorStateMatcher, CrewService, LaborService } from 'src/app/shared/services';

@Component({
    selector: 'app-project-crews',
    templateUrl: './crews.component.html',
    styleUrls: ['./crews.component.scss'],
    standalone: false
})
export class CrewsComponent implements OnInit {

  public loading: number = 0;
  public initialized: boolean = false;
  public editing: boolean = false;

  public pagination = {
    pageIndex: 0,
    length: 0,
    pageSize: 20,
  };

  @Input() public project: Project;
  public crews: Crew[] = [];
  public crew: Crew;
  public newCrew: Crew;

  public labors: Labor[] = [];

  public matcher = new AppErrorStateMatcher();
  @ViewChild('vcInputForm') vcInputForm;
  public inputForm: UntypedFormGroup = this.fb.group({
    id: [''],
    name: ['', [Validators.required]],
    labor_ids: ['', [Validators.required]],
  });

  constructor(
    public dialog: MatDialog,
    private fb: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private crewSrv: CrewService,
    private laborSrv: LaborService
  ) { }

  ngOnInit(): void {
    this.newCrew = new Crew({
      project: this.project
    });
    this.fetchRecords();
    this.fetchLabors();
  }

  pageChanged(event: PageEvent) {
    this.pagination = event;
    this.fetchRecords();
  }

  /**
   * Fetches Labor[]
   */
  fetchLabors(): void {
    this.loading++;
    const qp = {
      include: ['default_job_code']
    };
    this.laborSrv.getRecords(this.project, { pageIndex: 0, pageSize: 10000 }, qp)
      .then((resp: any) => {
        const { result } = resp;
        this.labors = result || [];
      })
      .catch((resp: HttpErrorResponse) => {
        this.snackBar.open(
          resp.error?.error || 'Oops! something went wrong.',
          '',
          { duration: 5000 },
        );
      })
      .finally(() => {
        this.loading--;
      });
  }

  /**
   * Fetches Labor[]
   */
  fetchRecords(): void {
    this.loading++;
    const qp = {
      include: [
        { labors: ['default_job_code'] },
      ]
    };
    this.crewSrv.getRecords(this.project, this.pagination, qp)
      .then((resp: any) => {
        const { result, meta } = resp;
        this.crews = (result || []).map(o => {
          o.project_id = this.project?.id;
          return o;
        });
        this.pagination.length = meta?.totalRecords || 0;
      })
      .catch((resp: HttpErrorResponse) => {
        this.snackBar.open(
          resp.error?.error || 'Oops! something went wrong.',
          '',
          { duration: 5000 },
        );
      })
      .finally(() => {
        this.loading--;
        this.initialized = true;
      });
  }

  /**
   * Toggles input form
   * @param model BaseModel
   * @param show boolean
   */
  toggleForm(model: Crew = null, show = true): void {
    this.crew = model?.id ? model : new Crew({ project: this.project });
    this.editing = show;

    if (show) {
      this.vcInputForm?.nativeElement?.reset();
      this.inputForm.reset({
        id: model?.id || null,
        name: model?.name || null,
        labor_ids: model?.labor_ids || [],
      });
    }
  }

  /**
   * Cancel update, and ignore form changes
   */
  onCancel() {
    this.toggleForm(null, false);
  }

  /**
   * Save Record
   * @returns {void}
   */
  save(form: UntypedFormGroup = null): void {
    if (!form.valid) {
      return;
    }

    const payload = form.value;
    payload.project_id = this.project?.id;
    this.loading++;

    const qp = { include: [{ labors: ['default_job_code'] }], };
    const req = payload?.id
      ? this.crewSrv.update(payload, qp)
      : this.crewSrv.create(payload, qp);

    req.then((crew: Crew) => {
      crew.project_id = this.project?.id;

      this.snackBar.open(
        `${this.crew?.id ? 'Updated' : 'Created'} ${crew?.name}`,
        '',
        { duration: 5000 },
      );


      const i = this.crews.findIndex(o => o.id === crew.id);
      if (i >= 0) {
        this.crews.splice(i, 1, crew);
      } else {
        this.crews.push(crew);
      }

      this.toggleForm(null, false);
    })
      .catch((resp: HttpErrorResponse) => {
        if (resp.status === 422) {
          this.matcher.setServerErrors(this.inputForm, resp);
          return;
        }
        this.snackBar.open(
          resp.error?.error || 'Oops! something went wrong.',
          '',
          { duration: 5000 },
        );
      })
      .finally(() => {
        this.loading--;
      });
  }

  /**
   * Deletes Record
   * @param model BaseModel
   */
  delete(model: Crew): void {
    const message = `Deleting <b>${model.name}</b> cannot be undone.
      <br />Proceed to delete?`;
    const dialogData = new ConfirmDialogModel('Delete?', message);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.crewSrv.delete(model)
        .then((resp: any) => {
          this.crews.splice(
            this.crews.findIndex(o => o.id === model.id),
            1,
          );
          this.snackBar.open(resp.message, '', { duration: 3000 });
        })
        .catch((resp: HttpErrorResponse) => {
          this.snackBar.open(
            resp.error?.error || 'Oops! something went wrong.',
            '',
            { duration: 5000 },
          );
        });
    });
  }

}
